import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

const INVITE_IMG = "/invitation.png";

export const Confirmation = ({ rsvp, party, onDone }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.origin;
  const shareText = `You're invited to ${party?.title || "The Great Fall Soup Exchange"} — ${party?.date_display || "Oct 3, 2026"} at ${party?.time_display || "5 PM"}! RSVP here: ${shareUrl}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Invitation copied — send it to a friend!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="notice-block overflow-hidden"
      data-testid="rsvp-confirmation"
    >
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative h-64 md:h-auto overflow-hidden">
          <img src={INVITE_IMG} alt="Your invitation" className="h-full w-full object-cover" data-testid="invitation-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-moss/40" />
          <p className="absolute top-5 left-5 font-accent text-3xl text-[#F6E9D2] -rotate-3 drop-shadow-lg">you're in!</p>
        </div>
        <div className="p-8 md:p-12">
          <h3 className="font-display text-3xl md:text-4xl text-cream leading-tight">
            See you there, <span className="italic text-ember">{rsvp.name.split(" ")[0]}</span>.
          </h3>
          <p className="mt-4 font-body text-base text-fog leading-relaxed">
            Party of <strong className="text-cream">{rsvp.party_size}</strong> — bringing{" "}
            <strong className="text-harvest">{rsvp.soups.join(" + ")}</strong>. Remember: one quart for the tasting table, the
            rest for the swap — take home as many as you share. Freezable containers, full ingredient list on
            every quart.
          </p>

          <div className="mt-8 border-t border-forest pt-8">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-fog mb-4">Know someone who'd love this?</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="invite-copy-btn" onClick={copyLink} className="btn-ember !px-6 !py-3 !text-xs">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied!" : "Copy Invite"}
              </button>
              <a
                data-testid="invite-text-btn"
                href={`sms:?&body=${encodeURIComponent(shareText)}`}
                className="btn-ghost !px-6 !py-3 !text-xs"
              >
                <MessageCircle size={16} />
                Text a Friend
              </a>
            </div>
          </div>

          <button
            data-testid="confirmation-done-btn"
            onClick={onDone}
            className="mt-8 font-body text-xs uppercase tracking-[0.25em] text-fog hover:text-ember transition-colors duration-300"
          >
            ← Back to the board
          </button>
        </div>
      </div>
    </motion.div>
  );
};
